import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Context for organization data from subdomain
export const OrganizationContext = createContext(null);

// Hook to get current subdomain
export const useSubdomain = () => {
  const hostname = window.location.hostname;
  
  // Extract subdomain from hostname
  // e.g., "gartly.simplly.com.au" -> "gartly"
  // e.g., "gartly.preview.emergentagent.com" -> "gartly" (for testing)
  // e.g., "localhost" -> null
  
  const parts = hostname.split('.');
  
  // Handle different environments
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    // Local development - check for subdomain query param
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('subdomain') || null;
  }
  
  // Production: *.simplly.com.au
  if (hostname.endsWith('.simplly.com.au')) {
    const subdomain = parts[0];
    // Exclude 'www' and 'app' as they're not org subdomains
    if (subdomain && !['www', 'app', 'api'].includes(subdomain)) {
      return subdomain;
    }
  }
  
  // Preview environment: *.preview.emergentagent.com
  // The first part before 'preview' might be the job ID, so we check for org subdomain differently
  if (hostname.includes('.preview.emergentagent.com')) {
    // For testing, use query param or localStorage
    const urlParams = new URLSearchParams(window.location.search);
    const savedSubdomain = localStorage.getItem('test_subdomain');
    return urlParams.get('subdomain') || savedSubdomain || null;
  }
  
  return null;
};

// Hook to resolve subdomain to organization
export const useOrganization = () => {
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const subdomain = useSubdomain();

  useEffect(() => {
    const resolveOrganization = async () => {
      if (!subdomain) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/resolve-subdomain/${subdomain}`);
        setOrganization(response.data);
        
        // Store in localStorage for persistence
        localStorage.setItem('current_organization', JSON.stringify(response.data));
      } catch (err) {
        console.error('Failed to resolve subdomain:', err);
        setError('Organization not found');
      } finally {
        setLoading(false);
      }
    };

    resolveOrganization();
  }, [subdomain]);

  return { organization, loading, error, subdomain };
};

// Provider component
export const OrganizationProvider = ({ children }) => {
  const orgData = useOrganization();

  return (
    <OrganizationContext.Provider value={orgData}>
      {children}
    </OrganizationContext.Provider>
  );
};

// Hook to use organization context
export const useCurrentOrganization = () => {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error('useCurrentOrganization must be used within OrganizationProvider');
  }
  return context;
};

// Helper to get organization branding
export const getOrganizationBranding = (organization) => {
  if (!organization) {
    return {
      primaryColor: '#00A3A1',
      secondaryColor: '#E94E89',
      name: 'Simplly',
      logoUrl: null
    };
  }

  return {
    primaryColor: organization.primary_color || '#00A3A1',
    secondaryColor: organization.secondary_color || '#E94E89',
    name: organization.name,
    logoUrl: organization.logo_url
  };
};

// Helper to generate subdomain URL
export const getSubdomainUrl = (subdomain) => {
  // Always use production URL for simplly.com.au domain
  return `https://${subdomain}.simplly.com.au`;
};

export default OrganizationContext;
